import { Button } from "../../customer/components";
import styles from "./create-service-request.module.scss";
import { useState } from "react";
import ServiceRequestBreadcrumbs from "./components/ServiceRequestBreadcrumbs";
import { steps } from "./informations";
import { ServiceRequestFormProvider, SectionFormData, useServiceRequestForm } from "./context/ServiceRequestFormContext";
import {
    SubmitServiceRequestAttributePayload,
    SubmitServiceRequestSectionPayload,
    useSupportAgentSubmitSRMutation,
    useSupportAgentSubmitSRWithCardMutation,
} from "../../../service/customersupport-service";
import { FieldType } from "../../../model/service-request/attribute";
import { toast } from "react-toastify";
import { getErrorMessage } from "../../../utils/error-handler";
import { useNavigate } from "react-router-dom";

const PAYMENT_STEP_INDEX = 3;

const buildApiSections = (sections: SectionFormData): SubmitServiceRequestSectionPayload[] =>
    Object.entries(sections)
        // Sections the agent never fills (no registered fields) must be omitted —
        // the backend rejects section instances without at least one attribute.
        .filter(([, fields]) => fields.length > 0)
        .map(([sectionId, fields]) => ({
        sectionDefinitionId: sectionId,
        attributes: fields.map((field): SubmitServiceRequestAttributePayload => {
            const attr: SubmitServiceRequestAttributePayload = { attributeDefinitionId: field.fieldId };
            switch (field.fieldType) {
                case FieldType.Dropdown:
                case FieldType.SingleSelect:
                case FieldType.ConditionalDropdown:
                case FieldType.ChildDropdown:
                    if (field.selectedChildIds && field.selectedChildIds.length > 0) {
                        attr.selectedOptionIds = field.selectedChildIds;
                    } else if (Array.isArray(field.fieldValue)) {
                        attr.selectedOptionIds = field.fieldValue.map(String);
                    } else if (field.selectedChildId || field.fieldValue) {
                        attr.selectedOptionIds = [field.selectedChildId || String(field.fieldValue)];
                    }
                    break;
                case FieldType.MultiSelect:
                    if (Array.isArray(field.fieldValue)) attr.selectedOptionIds = field.fieldValue.map(String);
                    break;
                case FieldType.DateTime:
                    if (field.fieldValue instanceof Date) attr.dateValue = field.fieldValue.toISOString();
                    else if (field.fieldValue) attr.dateValue = String(field.fieldValue);
                    break;
                case FieldType.Bool:
                    if (field.fieldValue != null) attr.boolValue = Boolean(field.fieldValue);
                    break;
                case FieldType.Number:
                    if (field.fieldValue != null) attr.doubleValue = typeof field.fieldValue === "number" ? field.fieldValue : parseFloat(field.fieldValue) || 0;
                    break;
                case FieldType.File:
                case FieldType.Image:
                    if (field.fieldValue instanceof File) attr.file = field.fieldValue;
                    break;
                default:
                    attr.stringValue = field.fieldValue?.toString() || "";
                    break;
            }
            return attr;
        }),
    }));



const ServiceRequestContent = () => {
    const [step, setStep] = useState(steps[0]);
    const [isPaymentDisabled, setIsPaymentDisabled] = useState(true);
    const [paymentUrl, setPaymentUrl] = useState<string | null>(null);
    const { form, validateStep } = useServiceRequestForm();
    const navigate = useNavigate();

    const [submitSR, { isLoading: isSubmitting }] = useSupportAgentSubmitSRMutation();
    const [submitSRWithCard, { isLoading: isSubmittingWithCard }] = useSupportAgentSubmitSRWithCardMutation();

    const isLoading = isSubmitting || isSubmittingWithCard ;

    const handleSubmit = async () => {
        const data = form.getValues();
        const basePayload = {
            serviceRequestId: data.requestTypeId,
            requestTypeId: data.requestTypeDefinitionId || data.requestTypeId,
            sections: buildApiSections(data.sections),
            customerDetails: {
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                phoneNumber: data.phone,
                address: data.homeAddress,
                meterNumber: data.meterNumber,
                meterType: data.meterType,
            },
            payWithLink: data.paymentMethod === "pay_with_link",
        };

        const isFree = !data.amount || data.amount.amount === 0;
        const isPayLater = data.payOnSubmit === false;

        const resetFlow = () => {
            form.reset();
            setStep(steps[0]);
            setIsPaymentDisabled(true);
            setPaymentUrl(null);
        };

        try {
            if (isFree || data.paymentMethod === null || isPayLater) {
                await submitSR(basePayload).unwrap();
                toast.success("Service request created successfully.");
                resetFlow();
                navigate("/util-admin/customer-support/all-instances");
            } else if (data.paymentMethod === "pay_now") {
                const returnUrl = window.location.href;
                const res = await submitSRWithCard({ ...basePayload, returnUrl}).unwrap();
                setPaymentUrl(res.url);
                toast.success("Service request created. Proceed with payment.");
                resetFlow();
                window.open(res.url);
            } else if (data.paymentMethod === "pay_with_link") {
                await submitSRWithCard({ ...basePayload, returnUrl: window.location.href }).unwrap();
                toast.success("Service request created. Payment link sent to customer.");
                resetFlow();
                navigate("/util-admin/customer-support/all-instances");
            }
        } catch (err: any) {
            toast.error(getErrorMessage(err?.data) ?? "Failed to create service request.");
        }
    };

    const handleContinue = async () => {
        const currentIndex = steps.indexOf(step);
        const isValid = await validateStep(currentIndex);
        if (!isValid) return;

        const nextIndex = currentIndex + 1;
        const isLastStep = currentIndex === steps.length - 1;
        const nextIsDisabledPayment = nextIndex === PAYMENT_STEP_INDEX && isPaymentDisabled;

        if (isLastStep || nextIsDisabledPayment) {
            handleSubmit();
            return;
        }

        if (nextIndex < steps.length) {
            setStep(steps[nextIndex]);
        }
    };

    const handlePrevious = () => {
        const currentIndex = steps.indexOf(step);
        if (currentIndex > 0) {
            setStep(steps[currentIndex - 1]);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1 className={styles.title}>{step.headerTittle}</h1>
                <div>
                    <ServiceRequestBreadcrumbs step={step} place="header" isPaymentDisabled={isPaymentDisabled} />
                </div>
                <div className={styles.headerBtns}>
                    <Button className={styles.previousBtn} onClick={handlePrevious} disabled={isSubmitting || isSubmittingWithCard}>
                        Previous
                    </Button>
                    <Button className={styles.continueBtn} onClick={handleContinue} disabled={isSubmitting || isSubmittingWithCard}>
                       {isSubmitting || isSubmittingWithCard ? 'Sending...' :  'Continue'}
                    </Button>
                </div>

            </div>

            <div className={styles.main}>
                <div className={styles.mainContent}>
                    <step.component title={step.title} message={step.message} setDisabled={setIsPaymentDisabled} />
                </div>
                <div className={styles.breadcrumbsContainer}>
                    <ServiceRequestBreadcrumbs step={step} place="main" isPaymentDisabled={isPaymentDisabled} />
                </div>
            </div>
        </div>
    );
};

const SupportAgentCreateServiceRequestPage = () => (
    <ServiceRequestFormProvider>
        <ServiceRequestContent />
    </ServiceRequestFormProvider>
);

export default SupportAgentCreateServiceRequestPage;
